import type {MedicalTransporterType, UserPermissionType} from '@ambler/prisma'
import {gql, useQuery} from '@apollo/client'
import type {ApolloError} from '@apollo/client'
import {clearConnectAsSessionId} from '@ambler/shared-front'
import {useMutation} from '../../hooks/use-mutation'
import {getUpdateDevicePayload} from '../../lib/firebase-messaging'
import {useAuthSession} from '../../lib/auth/session'
import type {AuthSession} from '../../lib/auth/session'
import type {BO_logoutDeviceMutation_, BO_logoutDeviceMutationVariables, BO_meQuery_} from './auth.generated'
import {useProfile} from './profile'

type MtAcl = {
  canRead: boolean
  canWrite: boolean
  mt: MedicalTransporter
}

const logoutDeviceMutation = gql`
  mutation BO_logoutDeviceMutation($data: PushNotificationsUpsertDeviceDataInput!) {
    pushNotificationsUpsertDevice(data: $data)
  }
`

// The identity comes from the API, which resolves it per request, so a permission
// change takes effect on the next one instead of waiting for a new token.
const meQuery = gql`
  query BO_meQuery {
    me {
      id
      auth0Id
      name
      email
      role
      isEmailConfirmed
      isConnectAs
      permissions {
        id
        type
      }
    }
  }
`

export const useAuth = (): {
  user: {
    id: string
    auth0Id: string
    name: string
    email: string
    role: string
  }
  mtAcls: MtAcl[]
  isAuthenticated: boolean
  isEmailConfirmed: boolean
  isLoading: boolean
  isConnectAs: boolean
  // The identity is fetched, so it can fail: consumers must be able to tell
  // "failed" from "loaded and empty", which look alike otherwise.
  identityError: ApolloError | undefined
  signIn: AuthSession['signIn']
  signOut: (redirectToHome?: boolean) => void
  permissions: {id: string; type: UserPermissionType}[]
} => {
  const [logoutDevice] = useMutation<BO_logoutDeviceMutation_, BO_logoutDeviceMutationVariables>({
    mutation: logoutDeviceMutation,
  })

  const {isAuthenticated, isLoading, signIn: sessionSignIn, signOut: sessionSignOut} = useAuthSession()

  const {
    data,
    loading: isMeLoading,
    error: identityError,
  } = useQuery<BO_meQuery_>(meQuery, {
    skip: isLoading || !isAuthenticated,
  })

  const {id, auth0Id, name, email, role, isEmailConfirmed, isConnectAs, permissions} = data?.me ?? {}

  const {mts, isLoading: isProfileLoading} = useProfile()

  return {
    user: {
      id,
      auth0Id,
      name,
      email,
      role,
    },
    mtAcls: mts.map(mtAcl => ({
      mt: mtAcl.mt,
      canRead: mtAcl.acl.canRead,
      canWrite: mtAcl.acl.canWrite,
    })),
    isAuthenticated,
    isEmailConfirmed,
    permissions,
    // Anything gating on the identity has to wait for this: an absent identity is
    // indistinguishable from a non-staff one.
    isLoading: isLoading || isMeLoading || isProfileLoading,
    isConnectAs,
    identityError,
    signIn: sessionSignIn,
    signOut: async () => {
      // Leaves the impersonation mode. Required because the per-tab id survives a
      // logout — a same-tab navigation — and the server session outlives it, so
      // signing back in here would silently resume impersonating.
      clearConnectAsSessionId()

      try {
        await logoutDevice({
          variables: {
            data: {
              ...(await getUpdateDevicePayload()),
              state: 'LOGGED_OUT',
              fcmToken: null,
            },
          },
        })
      } catch (_err) {
        // no op but we want the logout to happen
      }
      return sessionSignOut()
    },
  }
}

type MedicalTransporter = {
  id: string
  name?: string
  type: MedicalTransporterType
}

type MainMt = {
  canRead: boolean
  canWrite: boolean
  mt: MedicalTransporter
}

export const useMainMt = (): MainMt => {
  const {isLoading, isAuthenticated, mtAcls} = useAuth()

  if (isLoading || !isAuthenticated) {
    return null
  }

  return mtAcls?.[0]
}
